import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';

import { type ProvisionedMerchant, type ProvisionMerchantFormValues } from './schemas';

import { apiRequest, type ApiError } from '@/lib/api-client';
import { useSession } from '@/lib/session';

/**
 * `POST /api/v1/admin/organisations` - an operator (platform_admin, or a superadmin by hierarchy) creates a merchant: the owner's
 * account, the organisation and the owner membership. Enforced server-side by `@RequirePlatformRole('platform_admin')`, by the
 * re-authentication guard (`confirmPassword`) and, independently, by the database function behind it. This hook cannot and does not
 * try to check any of that: a client that decided who may call an endpoint would be deciding it for an attacker too.
 *
 * The answer carries the provisional password, ONCE: `gcTime: 0` so the mutation (and the password with it) is collected as soon as the
 * caller has read it, and the caller keeps it in component state only until the operator confirms they noted it.
 *
 * The merchant queue is invalidated on success because the new organisation appears in it immediately - pending or active depending
 * on what the operator chose.
 */
export function useProvisionMerchantMutation(): UseMutationResult<
  ProvisionedMerchant,
  ApiError,
  Omit<ProvisionMerchantFormValues, 'managedBy'> & { readonly managedBy?: string }
> {
  const { accessToken } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    gcTime: 0,
    mutationFn: (values) =>
      apiRequest<ProvisionedMerchant>('/admin/organisations', {
        method: 'POST',
        body: values,
        ...(accessToken === null ? {} : { accessToken }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['merchants'] });
    },
  });
}
